import {
  mdiChevronLeft,
  mdiHistory,
  mdiInformationOutline,
  mdiPrinter,
} from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAuth } from "../../../context_providers/auth/authProvider";
import DrawerWidget from "../../../context_providers/modal/drawer_widget";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ModalType,
  ModalAlignment,
} from "../../../context_providers/modal/modal_context";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { fetchPost, fetchGet } from "../../../service/api_client";
import { UTCToLocalDate } from "../../../utils/date_util";
import { ACTION, MODULE } from "../../../utils/enums";
import { currencyFormat } from "../../../utils/currency_format";
import {
  currencies,
  default_mail_detail,
} from "../../accounts/models/party_model";
import PartySearchField from "../../accounts/parties/party_search_field";
import WarehouseSearchField from "../../inventory/warehouse/warehouse_search_field";
import {
  auto_approval_amount,
  po_approval_status,
  getAprrovalLevel,
  PurchaseOrderModelV2,
  PoItemV2,
  po_default_value_v2,
  POType,
} from "../model/purchase_order_model";
import PoSnapSection from "./components/po_snaps";
import PoCreateBottomSheetWidget from "./components/bottom_sheet";
import { getExchangeRate } from "./service/purchase_order_repository";
import { usePurchaseOrder } from "../contexts/po-creation-context";
import KazamGSTSearchField from "./components/GSTSearchField";
import { POLineItemCard } from "./components/po_line_item_card";
import {
  createPurchaseOrderRepo,
  saveDraftPurchaseOrderRepo,
} from "./service/po_service";
import { Project } from "../../inventory/models/project_model";
import { urls } from "../../../utils/urls";
import ModalDialog from "../../../ui/new_components/common/modal";
import "../../../styles/responsive_shrink.css";
import Papa from "papaparse";

interface PurchaseOrderCreateScreenProps {}

const PurchaseOrderCreateScreen: FunctionComponent<
  PurchaseOrderCreateScreenProps
> = (props) => {
  const navigation = useHistory();
  const { user, isAuthorised, backdatedPOCreationAuthorised } = useAuth();
  const { purchaseOrder, setPurchaseOrder, setDefaultProject } =
    usePurchaseOrder();
  const { id } = useParams<{ id: string }>();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order`;
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [project, setProject] = useState<Project>();
  const [showNonExistItemsModal, setShowNonExistItemsModal] = useState(false);
  const [nonExistItems, setNonExistItems] = useState<string[]>([]);
  const [showNonExistProjectsModal, setShowNonExistProjectsModal] =
    useState(false);
  const [nonExistProjects, setNonExistProjects] = useState<string[]>([]);

  const [prvData, setPrvData] = useState<PurchaseOrderModelV2>({
    ...po_default_value_v2,
    shipping_profile: {
      ...default_mail_detail,
      contact_name: user?.first_name + " " + (user?.last_name || ""),
      contact_mobile: user?.mobile || "",
      contact_email: user?.email || "",
    },
  });
  const [addNewId, setAddNewId] = useState(Math.random() * 10000);
  const [disabled, setDisabled] = useState();
  const [isBackdatedPOCreationAllowed, setIsBackdatedPOCreationAllowed] =
    useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Programmatically trigger file input
    }
  };

  const handleCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ✅ Parse CSV to check if it has valid rows (excluding just headers)
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const data = results.data;

        if (!data || data.length === 0) {
          showToast({
            type: ToastType.error,
            text: "CSV must contain at least one data row.",
          });
          return;
        }

        // ✅ Proceed to upload only if CSV is valid
        const formData = new FormData();
        formData.append("file", file);

        const upload_url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order/import-items`;

        try {
          const res = await fetchPost(upload_url, formData);

          if (res.success) {
            setPurchaseOrder((o: PurchaseOrderModelV2) => ({
              ...o,
              items: [...o.items, ...res.data],
            }));
            showToast({ type: ToastType.success, text: res.message });
          } else {
            if (res.non_existent_items?.length > 0) {
              setNonExistItems(res.non_existent_items);
              setShowNonExistItemsModal(true);
            }

            if (res.non_existent_projects?.length > 0) {
              setNonExistProjects(res.non_existent_projects);
              setShowNonExistProjectsModal(true);
            }

            showToast({ type: ToastType.error, text: res.error });
          }
        } catch (err) {
          showToast({
            type: ToastType.error,
            text: "File upload failed. " + (err as any)?.message || "",
          });
          console.error(err);
        }
      },
      error: (err) => {
        showToast({
          type: ToastType.error,
          text: "Failed to read CSV. Please check the format.",
        });
        console.error("CSV Parse Error:", err);
      },
    });
  };

  const handleDownloadTemplate = () => {
    const headers = ["Item Code", "Qty", "Price", "Project Id"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "po_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (disabled === undefined) {
      let tmpDisabled =
        purchaseOrder.id &&
        purchaseOrder.approval_status === po_approval_status.approved &&
        purchaseOrder.approved_by;

      if (tmpDisabled) {
        const authorised = isAuthorised({
          action: [ACTION.UPDATE, ACTION.CREATE, ACTION.READ],
          module: MODULE.purchase_order_approval,
        });
        if (authorised) {
          tmpDisabled = false;
        }
      }
      setDisabled(tmpDisabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrder, purchaseOrder?.type]);

  const saveDraft = async () => {
    setLoading(true);

    try {
      const res = await saveDraftPurchaseOrderRepo(purchaseOrder);

      if (res.success) {
        setPurchaseOrder(res.data);
        showToast({
          type: ToastType.success,
          text: "Draft saved successfully!",
        });
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({ type: ToastType.error, text: "Failed to save draft." });
    } finally {
      setLoading(false);
      navigation.push(urls.purchase_order);
    }
  };

  const submit = async () => {
    if (
      !purchaseOrder.vendor_currency ||
      !purchaseOrder.vendor_currency_to_inr
    ) {
      return showToast({
        type: ToastType.error,
        text: "Select vendor currency and exchange rate",
      });
    }

    if (purchaseOrder.bill_to.gst_number === "") {
      return showToast({
        type: ToastType.error,
        text: "Please fill in GST number in billing details",
      });
    }

    if (purchaseOrder.type === "") {
      return showToast({
        type: ToastType.error,
        text: "Please fill PO Type",
      });
    }

    if (!purchaseOrder.warehouse.id || !purchaseOrder.party.name) {
      return showToast({
        type: ToastType.error,
        text: "Select warehouse and vendor",
      });
    }
    if (!purchaseOrder.items.length) {
      return showToast({
        type: ToastType.error,
        text: "Select items",
      });
    }

    if (!window.confirm("Are you sure ?")) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("purchaseOrder", JSON.stringify(purchaseOrder));
    const res = await createPurchaseOrderRepo(purchaseOrder);
    if (res.success) {
      purchaseOrder.project_id = project?.id;
      purchaseOrder.project = project?.name;
      setPurchaseOrder(res.data);
      showToast({ type: ToastType.success, text: res.message });
      navigation.goBack();
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  useEffect(() => {
    const backDatedPOPermission = backdatedPOCreationAuthorised({
      action: ACTION.CREATE,
      module: MODULE.purchase_order_approval,
    });

    setIsBackdatedPOCreationAllowed(backDatedPOPermission);

    if (id) getData(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backdatedPOCreationAuthorised]);

  const getCurrencyExchangeRate = async () => {
    try {
      setLoading(true);

      if (
        purchaseOrder.vendor_currency?.short_name ===
        currencies.rupee.short_name
      ) {
        setPurchaseOrder((o) => ({ ...o, vendor_currency_to_inr: 1 }));
        return;
      }

      const formattedDate = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"

      const exchangeRate = await getExchangeRate({
        from: "USD",
        to: "INR",
        date: formattedDate,
      });

      setPurchaseOrder((o) => ({
        ...o,
        vendor_currency_to_inr: exchangeRate ?? 0,
      }));
    } catch (error) {
      console.error("Error getting exchange rate:", error);
    } finally {
      setLoading(false);
    }
  };

  const getData = async (id: string) => {
    setLoading(true);
    const res = await fetchGet(url + "/" + id);
    if (res.success) {
      if (!res.data.vendor_currency) {
        res.data.vendor_currency = currencies.rupee;
        res.data.vendor_currency_to_inr = 1;
      }

      if (res.data.items === undefined || res.data.items === null) {
        res.data.items = [];
      }

      setPurchaseOrder({ ...res.data });
      setPrvData({ ...res.data });
      if (res.data.items[0].project) {
        setDefaultProject(res.data.items[0].project);
      }
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
    setLoading(false);
  };
  //----------------------------TAC CALCULATION---------------------
  useEffect(() => {
    purchaseOrder.subtotal = 0;
    purchaseOrder.sgst = 0;
    purchaseOrder.cgst = 0;
    purchaseOrder.igst = 0;

    if (Array.isArray(purchaseOrder.items) && purchaseOrder.items.length > 0) {
      purchaseOrder.items.forEach((item) => {
        const itemTotal = item.unit_no * item.price;
        purchaseOrder.subtotal += itemTotal;

        let gstRate = item.item.tax_detail?.gst_rate || 0;

        const taxAmount = gstRate ? (itemTotal * gstRate) / 100 : 0;

        const billToCountry = purchaseOrder.bill_to?.address?.country || "";
        const billToState = purchaseOrder.bill_to?.address?.state || "";
        const partyCountry =
          purchaseOrder.party?.mail_detail?.address?.country || "";
        const partyState =
          purchaseOrder.party?.mail_detail?.address?.state || "";

        const isSameCountryTransaction =
          billToCountry !== "" &&
          partyCountry !== "" &&
          billToCountry.toLowerCase() === partyCountry.toLowerCase();

        const isInterStateTransaction =
          billToState !== "" &&
          partyState !== "" &&
          billToCountry !== "" &&
          partyCountry !== "" &&
          billToState.toLowerCase() !== partyState.toLowerCase();

        if (isInterStateTransaction && isSameCountryTransaction) {
          if (purchaseOrder.vendor_currency.short_name !== "INR") {
            purchaseOrder.igst +=
              taxAmount / purchaseOrder.vendor_currency_to_inr;
            purchaseOrder.cgst = 0;
            purchaseOrder.sgst = 0;
          } else {
            purchaseOrder.igst += taxAmount;
            purchaseOrder.cgst = 0;
            purchaseOrder.sgst = 0;
          }
        }
        // same state and same country
        else if (!isInterStateTransaction && isSameCountryTransaction) {
          const sgst = taxAmount / 2.0;
          const cgst = taxAmount / 2.0;

          if (purchaseOrder.vendor_currency.short_name !== "INR") {
            purchaseOrder.sgst += sgst / purchaseOrder.vendor_currency_to_inr;
            purchaseOrder.cgst += cgst / purchaseOrder.vendor_currency_to_inr;
          } else {
            purchaseOrder.sgst += sgst;
            purchaseOrder.cgst += cgst;
          }
        }
        // state does not matter different country
        else {
          purchaseOrder.sgst = 0;
          purchaseOrder.cgst = 0;
          purchaseOrder.igst = 0;
        }

        const party = purchaseOrder.party;

        if (party.tax_detail.reg_type === "Unregistered") {
          purchaseOrder.cgst = 0;
          purchaseOrder.sgst = 0;
          purchaseOrder.igst = 0;
        }
      });
    }

    purchaseOrder.total =
      purchaseOrder.subtotal +
      (purchaseOrder.cgst ?? 0) +
      (purchaseOrder.sgst ?? 0) +
      (purchaseOrder.igst ?? 0);

    if (purchaseOrder.total <= auto_approval_amount) {
      // no approval required
      purchaseOrder.approval_required = false;
      purchaseOrder.approval_status = po_approval_status.approved;
    } else {
      // approval required
      if (prvData.approval_status === po_approval_status.approved) {
        // already approved
        if (purchaseOrder.total > prvData.total) {
          //new total > old total
          purchaseOrder.approval_required = true;
          purchaseOrder.approval_status = po_approval_status.pending;
        } else {
          purchaseOrder.approval_required = prvData.approval_required;
          purchaseOrder.approval_status = prvData.approval_status;
        }
      } else {
        // already not approved
        purchaseOrder.approval_required = prvData.approval_required;
        purchaseOrder.approval_status = prvData.approval_status;
      }
    }

    purchaseOrder.approval_level =
      purchaseOrder.vendor_currency.short_name === currencies.rupee.short_name
        ? getAprrovalLevel(purchaseOrder.total)
        : getAprrovalLevel(
            purchaseOrder.total * purchaseOrder.vendor_currency_to_inr,
          );
    setPurchaseOrder({ ...purchaseOrder });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    purchaseOrder.items,
    purchaseOrder.vendor_currency,
    purchaseOrder.vendor_currency_to_inr,
    purchaseOrder.bill_to,
    purchaseOrder.party,
  ]);

  const history = useHistory();

  const [showSnaps, setShowSnaps] = useState(false);
  const existingItems = purchaseOrder.items || [];

  useEffect(() => {
    if (purchaseOrder.vendor_currency) {
      getCurrencyExchangeRate();
    }
  }, [purchaseOrder.vendor_currency]);

  useEffect(() => {
    if (purchaseOrder.id && !project && purchaseOrder.project_id) {
      getProjectData(purchaseOrder.project_id);
    }
  }, [purchaseOrder]);

  const getProjectData = async (id: string) => {
    setLoading(true);

    // Project Master (new) first, then legacy procurement projects for older POs
    let res = await fetchGet(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/project/${id}`,
    );
    if (!res.success) {
      res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/project/${id}`,
      );
    }
    if (res.success) {
      setProject({ ...res.data });
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  return (
    <>
      {showNonExistItemsModal && (
        <ModalDialog
          id="po_create_non_exist_items_modal"
          show={showNonExistItemsModal}
          title="Items Not Exists"
          onClose={() => setShowNonExistItemsModal(false)}
        >
          <div className="p-4">
            <pre className="bg-gray-100 p-2 rounded text-red-600 whitespace-pre-wrap">
              <div className="modal-content">
                <h2>Rejected Upload Items</h2>
                <ul>
                  {nonExistItems.map((id: any, index: any) => (
                    <li key={index}>{id}</li>
                  ))}
                </ul>
              </div>
            </pre>
          </div>
        </ModalDialog>
      )}
      {showNonExistProjectsModal && (
        <ModalDialog
          id="po_create_non_exist_projects_modal"
          show={showNonExistProjectsModal}
          title="Projects Not Exists"
          onClose={() => setShowNonExistProjectsModal(false)}
        >
          <div className="p-4">
            <pre className="bg-gray-100 p-2 rounded text-red-600 whitespace-pre-wrap">
              <div className="modal-content">
                <h2>Rejected Upload Projects</h2>
                <ul>
                  {nonExistProjects.map((id: any, index: any) => (
                    <li key={index}>{id}</li>
                  ))}
                </ul>
              </div>
            </pre>
          </div>
        </ModalDialog>
      )}
      {showSnaps && purchaseOrder.id && (
        <DrawerWidget
          id="po_create_snaps_drawer"
          data={{
            id: 1,
            title: "Purchase order prints",
            type: ModalType.drawer,
            alignment: ModalAlignment.right,
            container: <PoSnapSection po_no={purchaseOrder.po_no} />,
          }}
          onClose={function (): void {
            setShowSnaps(false);
          }}
        ></DrawerWidget>
      )}

      <div className="mobile-shrink relative bg-myBgcolor h-screen px-2 md:px-5  overflow-auto space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between py-2 md:sticky md:top-0 bg-myBgcolor gap-2 z-20">
          <div className="flex flex-row items-center">
            <button
              id="po_create_back_btn"
              className="flex items-center p-2 hover:text-gray-700 font-bold text-lg"
              onClick={() => {
                history.goBack();
              }}
            >
              <Icon path={mdiChevronLeft} className="h-6 w-6 font-bold"></Icon>
              <span className="hidden md:inline">Back</span>
            </button>
            <div className="text-xl font-bold border-l-4 border-myPrimaryColor p-2">
              Purchase Order{" "}
              <span className="text-sm">#{purchaseOrder.po_no}</span>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="flex gap-2 items-center">
              <label htmlFor="" className="text-sm font-semibold text-gray-500">
                Date
              </label>
              <input
                id="po_create_date"
                type="date"
                disabled={!isBackdatedPOCreationAllowed}
                className="focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                value={
                  purchaseOrder.date
                    ? moment(purchaseOrder.date).format("YYYY-MM-DD")
                    : ""
                }
                onChange={(e) => {
                  const date = UTCToLocalDate(e.target.value);
                  if (date) {
                    setPurchaseOrder((prev: PurchaseOrderModelV2) => ({
                      ...prev,
                      date,
                    }));
                  }
                }}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {purchaseOrder.id && (
                <button
                  id="po_create_history_btn"
                  title="Logs"
                  onClick={() => {
                    // showLog()
                  }}
                  className="rounded-full p-2 hover:scale-105 transform duration-150 bg-myPrimaryColor text-white shadow cursor-pointer flex items-center justify-center"
                >
                  <Icon path={mdiHistory} className="h-6 w-6  "></Icon>
                </button>
              )}
            </div>
          </div>
        </div>
        {purchaseOrder.approval_status === po_approval_status.rejected &&
          purchaseOrder.commets.length > 0 && (
            <p>
              <b>Reason of rejection :</b>
              {purchaseOrder.commets[purchaseOrder.commets.length - 1].comment}
            </p>
          )}

        <div className="bg-white rounded-lg px-3 py-5 po-form">
          <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 md:w-1/3">
            <label htmlFor="po_type" className="text-sm font-semibold">
              PO Type
            </label>
            <select
              disabled={disabled}
              required
              className="p-1 focus:outline-none border rounded w-full"
              id="po_type"
              name="po_type"
              value={purchaseOrder.type}
              onChange={(e) => {
                setPurchaseOrder((o: PurchaseOrderModelV2) => ({
                  ...o,
                  type: e.target.value,
                }));
              }}
            >
              <option value="" hidden>
                select
              </option>
              {Object.values(POType).map((c, i) => (
                <option value={c} key={i}>
                  {c}
                </option>
              ))}
            </select>
            {purchaseOrder.type === POType["Job Work PO"] && (
              <p className="text-sm font-semibold tracking-widest text-blue-700 mb-2">
                Job Work PO mode active: Split totals for Inventory and Payable
                Services. Only Service lines will attract GST.
              </p>
            )}
          </div>
        </div>
        <div className="bg-white rounded-lg px-3 py-5 po-form space-y-4">
          <div>
            <label htmlFor="" className="text-sm font-semibold">
              Vendor
            </label>
            <div className="po-search-field">
              <PartySearchField
                id="po_create_vendor_search"
                disabled={disabled}
                placeholder="Search vendor by name"
                value={purchaseOrder.party ? purchaseOrder.party : undefined}
                onSelect={(d) => {
                  setPurchaseOrder((o: PurchaseOrderModelV2) => {
                    const t = {
                      ...o,
                      party_id: d.id,
                      party: d,

                      party_mail_detail: d.mail_detail,
                    };

                    if (d.currency) {
                      t.vendor_currency = d.currency;
                    }
                    return t;
                  });
                }}
                status="Approved"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <label className="">Vendor Registered Office</label>
              <input
                id="po_create_vendor_line1"
                disabled={true}
                type="text"
                placeholder="Enter Registerd Address"
                value={purchaseOrder.party.mail_detail.address.line1}
                onChange={(e) => {
                  setPurchaseOrder((o) => ({
                    ...o,
                    party: {
                      ...o.party,
                      mail_detail: {
                        ...o.party.mail_detail,
                        address: {
                          ...o.party.mail_detail.address,
                          line1: e.target.value,
                        },
                      },
                    },
                  }));
                }}
                className="p-1 focus:outline-none border rounded w-full "
              />
            </div>
            <div>
              <label className="">State</label>
              <input
                id="po_create_vendor_state"
                disabled={true}
                type="text"
                placeholder="Enter State"
                value={purchaseOrder.party.mail_detail.address.state}
                onChange={(e) => {
                  setPurchaseOrder((o) => ({
                    ...o,
                    party: {
                      ...o.party,
                      mail_detail: {
                        ...o.party.mail_detail,
                        address: {
                          ...o.party.mail_detail.address,
                          state: e.target.value,
                        },
                      },
                    },
                  }));
                }}
                className="p-1 focus:outline-none border rounded w-full "
              />
            </div>
            <div>
              <label className="">Line 2</label>
              <input
                id="po_create_vendor_line2"
                disabled={true}
                type="text"
                placeholder="Enter Line 2"
                value={purchaseOrder.party.mail_detail.address.line2}
                onChange={(e) => {
                  setPurchaseOrder((o) => ({
                    ...o,
                    party: {
                      ...o.party,
                      mail_detail: {
                        ...o.party.mail_detail,
                        address: {
                          ...o.party.mail_detail.address,
                          line2: e.target.value,
                        },
                      },
                    },
                  }));
                }}
                className="p-1 focus:outline-none border rounded w-full "
              />
            </div>
            <div>
              <label className="">City</label>
              <input
                id="po_create_vendor_city"
                disabled={true}
                type="text"
                placeholder="Enter City"
                value={purchaseOrder.party.mail_detail.address.city}
                onChange={(e) => {
                  setPurchaseOrder((o) => ({
                    ...o,
                    party: {
                      ...o.party,
                      mail_detail: {
                        ...o.party.mail_detail,
                        address: {
                          ...o.party.mail_detail.address,
                          city: e.target.value,
                        },
                      },
                    },
                  }));
                }}
                className="p-1 focus:outline-none border rounded w-full "
              />
            </div>
            <div>
              <label className="">Country</label>
              <input
                id="po_create_vendor_country"
                disabled={true}
                type="text"
                placeholder="Enter Country"
                value={purchaseOrder.party.mail_detail.address.country}
                onChange={(e) => {
                  setPurchaseOrder((o) => ({
                    ...o,
                    party: {
                      ...o.party,
                      mail_detail: {
                        ...o.party.mail_detail,
                        address: {
                          ...o.party.mail_detail.address,
                          country: e.target.value,
                        },
                      },
                    },
                  }));
                }}
                className="p-1 focus:outline-none border rounded w-full "
              />
            </div>
            <div>
              <label className="">Pincode</label>
              <input
                id="po_create_vendor_pincode"
                disabled={true}
                type="text"
                placeholder="Enter Pincode"
                value={purchaseOrder.party.mail_detail.address.pincode}
                onChange={(e) => {
                  setPurchaseOrder((o) => ({
                    ...o,
                    party: {
                      ...o.party,
                      mail_detail: {
                        ...o.party.mail_detail,
                        address: {
                          ...o.party.mail_detail.address,
                          pincode: e.target.value,
                        },
                      },
                    },
                  }));
                }}
                className="p-1 focus:outline-none border rounded w-full "
              />
            </div>
            <div>
              <label className="">GST Reg</label>
              <input
                id="po_create_vendor_gst_reg"
                disabled={true}
                type="text"
                placeholder="Enter GST Reg"
                value={purchaseOrder.party.tax_detail.reg_type ?? "--"}
                onChange={(e) => {
                  setPurchaseOrder((o) => ({
                    ...o,
                    party: {
                      ...o.party,
                      tax_detail: {
                        ...o.party.tax_detail,
                      },
                    },
                  }));
                }}
                className="p-1 focus:outline-none border rounded w-full "
              />
            </div>
            <div>
              <label className="">GST No</label>
              <input
                id="po_create_vendor_gst_no"
                disabled={true}
                type="text"
                placeholder="Enter GST Reg"
                value={purchaseOrder.party.tax_detail.gst_no[0] ?? "--"}
                onChange={(e) => {}}
                className="p-1 focus:outline-none border rounded w-full "
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg px-3 py-5 po-form">
            <h1>Bill To</h1>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold">GST No</label>
                <div
                  id="po_create_bill_to_gst_search"
                  className="po-search-field"
                >
                  <KazamGSTSearchField
                    disabled={disabled}
                    placeholder={"Search bill-to by GST number"}
                    value={
                      purchaseOrder.bill_to?.id
                        ? purchaseOrder.bill_to
                        : undefined
                    }
                    onSelect={(d) => {
                      setPurchaseOrder((prev: PurchaseOrderModelV2) => ({
                        ...prev,
                        bill_to: {
                          ...d,
                          name: "Kazam EV Tech Pvt Ltd",
                        },
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <label className="">Name</label>
                  <input
                    id="po_create_bill_to_name"
                    disabled={true}
                    type="text"
                    placeholder="Name"
                    value={
                      purchaseOrder.bill_to?.name ?? "Kazam EV Tech Pvt Ltd"
                    }
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">State</label>
                  <input
                    id="po_create_bill_to_state"
                    disabled={true}
                    type="text"
                    placeholder="State"
                    value={purchaseOrder.bill_to?.address.state ?? "--"}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">Address</label>
                  <input
                    id="po_create_bill_to_line1"
                    disabled={true}
                    type="text"
                    placeholder="Enter Registerd Address"
                    value={purchaseOrder.bill_to?.address.line1 ?? "--"}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">City</label>
                  <input
                    id="po_create_bill_to_city"
                    disabled={true}
                    type="text"
                    placeholder="City"
                    value={purchaseOrder.bill_to?.address.city ?? "--"}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">Line 2</label>
                  <input
                    id="po_create_bill_to_line2"
                    disabled={true}
                    type="text"
                    placeholder="Enter Line 2"
                    value={purchaseOrder.bill_to?.address.line2 ?? "--"}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">Pincode</label>
                  <input
                    id="po_create_bill_to_pincode"
                    disabled={true}
                    type="text"
                    placeholder="Pincode"
                    value={purchaseOrder.bill_to?.address.pincode ?? "--"}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">Country</label>
                  <input
                    id="po_create_bill_to_country"
                    disabled={true}
                    type="text"
                    placeholder="Enter Country"
                    value={purchaseOrder.bill_to?.address.country ?? "--"}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg px-3 py-5 po-form">
            <h1>Shipping Profile</h1>
            <div className="space-y-4">
              <div>
                <label htmlFor="" className="text-sm font-semibold  ">
                  Ship to
                </label>
                <div className="po-search-field">
                  <WarehouseSearchField
                    id="po_create_ship_to_search"
                    // type={WAREHOUSE_TYPE.internal}
                    disabled={disabled}
                    placeholder="Search ship-to warehouse"
                    value={
                      purchaseOrder.warehouse.id
                        ? purchaseOrder.warehouse
                        : undefined
                    }
                    onSelect={(d) => {
                      setPurchaseOrder((prev: PurchaseOrderModelV2) => ({
                        ...prev,
                        warehouse: d,
                        warehouse_id: d.id,
                        shipping_profile: {
                          ...prev.shipping_profile, // Ensure we keep existing data
                          contact_name: "--",
                          contact_mobile: "--",
                          contact_email: "--",

                          address: d.address!,
                        },
                      }));
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <label className="">Address</label>
                  <input
                    id="po_create_ship_to_line1"
                    disabled={true}
                    type="text"
                    placeholder="Address"
                    value={purchaseOrder.shipping_profile.address.line1}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">State</label>
                  <input
                    id="po_create_ship_to_state"
                    disabled={true}
                    type="text"
                    placeholder="State"
                    value={purchaseOrder.shipping_profile.address.state}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">Line 2</label>
                  <input
                    id="po_create_ship_to_line2"
                    disabled={true}
                    type="text"
                    placeholder="Line 2"
                    value={purchaseOrder.shipping_profile.address.line2}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">City</label>
                  <input
                    id="po_create_ship_to_city"
                    disabled={true}
                    type="text"
                    placeholder="City"
                    value={purchaseOrder.shipping_profile.address.city}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">Country</label>
                  <input
                    id="po_create_ship_to_country"
                    disabled={true}
                    type="text"
                    placeholder="Country"
                    value={purchaseOrder.shipping_profile.address.country}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
                <div>
                  <label className="">Pincode</label>
                  <input
                    id="po_create_ship_to_pincode"
                    disabled={true}
                    type="text"
                    placeholder="Pincode"
                    value={purchaseOrder.shipping_profile.address.pincode}
                    className="p-1 focus:outline-none border rounded w-full "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg px-3 py-5 w-full md:w-1/3 po-form">
          <label>Currency</label>
          <div className="">
            <select
              disabled={disabled}
              required
              className="p-1 focus:outline-none border rounded  w-full"
              id="currency"
              name="currency"
              value={
                purchaseOrder.vendor_currency &&
                JSON.stringify(purchaseOrder.vendor_currency)
              }
              onChange={(e) => {
                setPurchaseOrder((o: PurchaseOrderModelV2) => {
                  const updatedOrder = {
                    ...o,
                    vendor_currency: JSON.parse(e.target.value),
                  };
                  return updatedOrder;
                });
              }}
            >
              <option value="" hidden>
                select
              </option>
              {Object.values(currencies).map((currency: any) => (
                <option value={JSON.stringify(currency)}>
                  {currency.formal_name}({currency.symbol})
                </option>
              ))}
            </select>
          </div>
          <label>Currency Exchange rate({"\u20b9"})</label>
          <div className="">
            <input
              id="po_create_exchange_rate"
              disabled={disabled}
              type="number"
              placeholder="Exchange rate of inr"
              value={purchaseOrder.vendor_currency_to_inr}
              onChange={(e) => {
                setPurchaseOrder((o: PurchaseOrderModelV2) => ({
                  ...o,
                  vendor_currency_to_inr: Number(e.target.value),
                }));
              }}
              className="p-1 focus:outline-none border rounded  w-full"
            />
            <div className="flex gap-1 text-gray-500 text-xs mt-2 items-center">
              <Icon path={mdiInformationOutline} size={0.8}></Icon>
              INR Per {purchaseOrder.vendor_currency.short_name} Eg: INR 70 =
              USD 1 then enter 70
            </div>{" "}
          </div>
        </div>
        <div className="">
          <div className="flex gap-2 items-center justify-between ">
            <h3 className="text-sm font-bold">Items</h3>
            <div className="">
              <input
                id="po_create_csv_file_input"
                type="file"
                ref={fileInputRef}
                accept=".csv"
                onChange={handleCSVUpload}
                className="hidden"
              />

              <label
                id="po_create_download_template_btn"
                onClick={handleDownloadTemplate}
                className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 m-1"
              >
                Template
              </label>

              <button
                id="po_create_upload_csv_btn"
                onClick={handleButtonClick}
                className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 my-1"
              >
                Upload CSV
              </button>
            </div>
          </div>
          <div className="mt-2 flex flex-col gap-3">
            {purchaseOrder.items.map((item, i) => {
              return (
                <div id={`po_create_item_row_${item.id}`} key={item.id}>
                  <POLineItemCard
                    variant="regular"
                    mode={disabled ? "view" : "edit"}
                    currency={purchaseOrder.vendor_currency}
                    data={item}
                    onDelete={(d) => {
                      setPurchaseOrder((o) => ({
                        ...o,
                        items: [...(o.items || [])].filter(
                          (i) => i.id !== d.id,
                        ),
                      }));
                    }}
                    onSubmit={function (data: PoItemV2): void {
                      setPurchaseOrder((o: PurchaseOrderModelV2) => ({
                        ...o,
                        items: [
                          ...(o.items || []).map((it) => {
                            if (it.id === data.id) return data;
                            return it;
                          }),
                        ],
                      }));
                    }}
                    existingItems={existingItems}
                  />
                </div>
              );
            })}
            {!disabled && (
              <div id="po_create_item_row_new" key={addNewId}>
                <POLineItemCard
                  variant="regular"
                  mode="create"
                  currency={purchaseOrder.vendor_currency}
                  onSubmit={function (purchaseOrder: PoItemV2): void {
                    purchaseOrder.id = (Math.random() * 1000).toString();
                    setPurchaseOrder((o: PurchaseOrderModelV2) => ({
                      ...o,
                      items: [...(o.items || []), purchaseOrder],
                    }));
                    setAddNewId(Math.random() * 1000);
                  }}
                  existingItems={existingItems}
                />
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 md:divide-x-2  text-sm  bg-white rounded-lg p-3  ">
          <div className=" flex flex-col gap-1 md:pr-5 ">
            <div className="grid grid-cols-1  md:grid-cols-4   dark: items-center">
              <label htmlFor="delivery_date">Delivery Date *</label>
              <input
                // disabled={disabled}
                value={
                  purchaseOrder.delivery_date
                    ? moment(purchaseOrder.delivery_date).format("YYYY-MM-DD")
                    : ""
                }
                onChange={(e) => {
                  setPurchaseOrder((o: any) => ({
                    ...o,
                    delivery_date: UTCToLocalDate(e.target.value)!,
                  }));
                }}
                type="date"
                name=""
                id="delivery_date"
                className="p-1 focus:outline-none border rounded w-full  col-span-3"
              ></input>
            </div>
            <div className="grid grid-cols-1  md:grid-cols-4  items-start">
              <label htmlFor="ship_term">Delivery terms</label>
              <textarea
                disabled={disabled}
                rows={3}
                value={purchaseOrder.ship_term}
                onChange={(e) => {
                  const sanitizedValue = e.target.value.replace(/[\n\r]/g, ""); // Remove newlines
                  setPurchaseOrder((o: any) => ({
                    ...o,
                    ship_term: sanitizedValue,
                  }));
                }}
                name=""
                id="ship_term"
                className="p-1 focus:outline-none border rounded w-full  col-span-3"
              ></textarea>
            </div>
            <div className="grid grid-cols-1  md:grid-cols-4  items-start">
              <label htmlFor="payment_term">Payment terms</label>
              <textarea
                // disabled={disabled}
                rows={3}
                value={purchaseOrder.payment_term}
                onChange={(e) => {
                  const sanitizedValue = e.target.value.replace(/[\n\r]/g, ""); // Remove newlines
                  setPurchaseOrder((o: any) => ({
                    ...o,
                    payment_term: sanitizedValue,
                  }));
                }}
                name=""
                id="payment_term"
                className="p-1 focus:outline-none border rounded w-full  col-span-3"
              ></textarea>
            </div>
          </div>

          <div className=" flex flex-col gap-1 md:pl-5 ">
            <div className="flex justify-between">
              <div className="">Subtotal</div>
              <div className="">
                {currencyFormat(
                  purchaseOrder.subtotal,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>{" "}
            <div className="flex justify-between ">
              <div className="flex  gap-2 items-end ">
                <label htmlFor="tax_desc">CGST </label>{" "}
              </div>

              <div className="">
                +{" "}
                {currencyFormat(
                  purchaseOrder.cgst,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <div className="flex justify-between ">
              <div className="flex  gap-2 items-end ">
                <label htmlFor="tax_desc">SGST </label>{" "}
              </div>

              <div className="">
                +{" "}
                {currencyFormat(
                  purchaseOrder.sgst,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <div className="flex justify-between ">
              <div className="flex  gap-2 items-end ">
                <label htmlFor="tax_desc">IGST</label>{" "}
              </div>

              <div className="">
                +{" "}
                {currencyFormat(
                  purchaseOrder.igst,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="">Paid</div>
              <div className="">
                {currencyFormat(
                  purchaseOrder.paid,
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <hr />
            <div className="flex justify-between font-bold ">
              <p>Total</p>{" "}
              <div className="">
                {currencyFormat(
                  purchaseOrder.subtotal +
                    (purchaseOrder?.cgst +
                      purchaseOrder?.sgst +
                      purchaseOrder?.igst -
                      purchaseOrder?.paid),
                  purchaseOrder.vendor_currency.short_name,
                )}
              </div>
            </div>
            <hr />
            {/* <div className="flex justify-between font-bold ">
              <p>Paid</p>{" "}
              <div className="">
                {currencyFormat(
                  purchaseOrder.paid,
                  purchaseOrder.vendor_currency.short_name
                )}
              </div>
            </div> */}
            <hr />
            {/* <div className="flex justify-between font-bold ">
              <p>Balance</p>{" "}
              <div className="">
                {currencyFormat(
                  purchaseOrder.total,
                  purchaseOrder.vendor_currency.short_name
                )}
              </div>
            </div> */}
            {purchaseOrder.vendor_currency.short_name !==
              currencies.rupee.short_name && (
              <div className="flex justify-between font-bold ">
                <p>Total in INR</p>{" "}
                <div className="">
                  {currencyFormat(
                    purchaseOrder.total * purchaseOrder.vendor_currency_to_inr,
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 right-0 w-full rounded-t-md bg-white shadow-sm py-3 px-5 border">
          <h3 className="text-sm font-semibold">Approval status</h3>

          <PoCreateBottomSheetWidget
            data={purchaseOrder}
            onSubmit={submit}
            onSaveDraft={saveDraft}
          />
        </div>
      </div>
      {id && (
        <div className="fixed flex flex-col gap-2 bottom-10 pb-5 right-3">
          {purchaseOrder.approval_level ===
            purchaseOrder.current_approval_level &&
            (purchaseOrder.current_approval_count ?? 0) >=
              (purchaseOrder.current_approval_required_count ?? 0) && (
              <button
                id="po_create_print_btn"
                title="print"
                // to={`${window.document.location.pathname}/comment`}
                onClick={() => setShowSnaps(true)}
                className="  rounded-full p-2  hover:scale-105 transform duration-150 bg-green-400 text-white   shadow cursor-pointer flex items-center justify-center"
              >
                <Icon
                  path={mdiPrinter}
                  size={1}
                  className="hover:scale-105 transform  "
                ></Icon>
              </button>
            )}
        </div>
      )}
      {loading && <LoadingWidget />}
    </>
  );
};

export default PurchaseOrderCreateScreen;
